/**
 * A gallery: one card per row, laid out across the width and scrolled.
 *
 * The same row window the grid uses, against the gallery endpoint. What differs
 * is only the arithmetic: a grid puts one row on each line, a gallery puts
 * `perRow` of them, so the window is told what a row costs in pixels of scroll
 * rather than how tall a row is. Everything else -- the buffer, the scroll
 * policy, realtime, the row dialog -- is shared, and shared by using it rather
 * than by resembling it.
 *
 * Read-only for now: a card shows a row and opens it, and the row dialog is
 * where it is changed. Card-level editing arrives with the rest of Phase B.
 */
import { useEffect, useRef, useState, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { rowColors } from '../decorations/colors'
import { useFieldContext } from '../fields/ui/context'
import { RowModal } from '../row/row-modal'
import {
  useRegisteredRowWindow,
  useRowQuery,
  useRowWindow,
  useRowWindowStore,
} from '../grid/use-grid'
import type { RowSearch } from '../rows/rowQuery'
import { rowAt } from '../rows/rowWindow'
import { hiddenFields, visibleFields, type FieldPlacement } from '../views/field-order'
import { RowCard } from '../views/row-card'
import { useRowDialog } from '../views/use-row-dialog'
import { fetchGalleryRows } from './fetcher'
import { cardPosition, galleryGeometry } from './layout'

export interface GalleryViewProps {
  view: ViewOf<'gallery'>
  fields: readonly Field[]
  fieldOptions: Record<string, FieldPlacement>
  search: RowSearch
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
}

export function GalleryView({
  view,
  fields,
  fieldOptions,
  search,
  openRowId,
  onOpenRow,
}: GalleryViewProps) {
  const { t } = useTranslation()
  const store = useRowWindowStore(fetchGalleryRows)
  useRowQuery(store, view, search)
  const member = useRegisteredRowWindow(store, {
    tableId: view.table_id,
    viewId: view.id,
    fields,
    view,
  })

  const context = useFieldContext(fields[0]?.workspace_id)
  const scroller = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  const count = useRowWindow(store, (state) => state.window.count)
  const visible = useRowWindow(store, (state) => state.visible)
  const window = useRowWindow(store, (state) => state.window)
  const answered = useRowWindow(store, (state) => state.answered)

  const shown = visibleFields(fields, fieldOptions)
  const coverField =
    fields.find((field) => field.id === view.card_cover_image_field) ?? null
  const geometry = galleryGeometry({
    width: size.width,
    count,
    fieldCount: shown.length,
    hasCover: coverField !== null,
  })

  // Measured rather than assumed, and on every resize: the window's padding is
  // half a viewport, so a container whose height is wrong holds the wrong number
  // of rows -- and a container whose width is wrong lays out the wrong number of
  // cards across, which changes how many rows a screenful is.
  useEffect(() => {
    const element = scroller.current
    if (element === null) return

    const report = () => {
      setSize({ width: element.clientWidth, height: element.clientHeight })
    }
    report()

    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  // Separate from the measuring, because the number it depends on is derived
  // from the measurement rather than part of it.
  useEffect(() => {
    store
      .getState()
      .setViewport({ height: size.height, rowHeight: geometry.scrollPerRow })
  }, [store, size.height, geometry.scrollPerRow])

  const openRow = useRowDialog({
    window,
    count,
    member,
    view,
    fields,
    openRowId,
    onOpenRow,
    search,
  })

  const decorations = view.decorations ?? []

  const indexes: number[] = []
  for (let index = visible.start; index < visible.end; index += 1) indexes.push(index)

  return (
    <>
      <div
        ref={scroller}
        onScroll={(event: UIEvent<HTMLDivElement>) => {
          store.getState().scrolled(event.currentTarget.scrollTop)
        }}
        className="h-full overflow-auto p-3"
        aria-label={t('gallery.label')}
        // A list rather than a grid: the cards are one sequence, and how many
        // of them fit on a line is a fact about the window's width rather than
        // about the view. Announcing columns that move when the sidebar is
        // collapsed would be announcing the furniture.
        role="list"
      >
        {/* Only once the server has said so: a window that has not been
            answered yet has a count of zero too, and telling somebody their
            table is empty while their rows are on the way is worse than a
            moment of nothing. */}
        {count === 0 && answered ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{t('gallery.emptyTitle')}</EmptyTitle>
              <EmptyDescription>{t('gallery.emptyDescription')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="relative" style={{ height: geometry.totalHeight }}>
            {indexes.map((index) => {
              const row = rowAt(window, index)
              const { left, top } = cardPosition(index, geometry)
              return (
                <div
                  // The index rather than the row id, because a slot the window
                  // is not holding yet has no row and still has a place.
                  key={index}
                  role="listitem"
                  aria-posinset={index + 1}
                  aria-setsize={count}
                  className="absolute"
                  style={{
                    left,
                    top,
                    width: geometry.cardWidth,
                    height: geometry.cardHeight,
                  }}
                >
                  {row === undefined ? (
                    // A card the window has asked for and not been given yet.
                    // Drawn rather than left out, so the gallery does not open
                    // holes in itself while somebody scrolls through it.
                    <div
                      data-testid={`card-placeholder-${index}`}
                      className="bg-muted/40 h-full w-full animate-pulse rounded-lg border"
                    />
                  ) : (
                    <RowCard
                      row={row}
                      fields={shown}
                      coverField={coverField}
                      context={context}
                      colors={rowColors(decorations, row, fields, context)}
                      onOpen={() => {
                        onOpenRow(row.id)
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {openRowId !== null && (
        <RowModal
          member={openRow.member}
          rowId={openRowId}
          row={openRow.row}
          missing={openRow.missing}
          fields={shown}
          hidden={hiddenFields(fields, fieldOptions)}
          context={context}
          steps={openRow.steps}
          onStep={openRow.onStep}
          onClose={() => {
            onOpenRow(null)
          }}
        />
      )}
    </>
  )
}
